<template>
  <div
    v-if="show && site"
    class="site-popup-overlay"
    @click.self="$emit('close')"
  >
    <div class="site-popup-content">
      <!-- Header -->
      <div class="site-popup-header">
        <div>
          <h3 class="site-popup-title">{{ site.building.address_line_1 }}</h3>
          <p class="site-popup-subtitle">Site Details</p>
        </div>
        <button @click="$emit('close')" class="site-popup-close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <!-- Body -->
      <div class="site-popup-body">
        <!-- Site Image -->
        <div v-if="siteImage" class="site-image-container">
          <!-- Loading Spinner -->
          <div v-if="imageLoading" class="image-loader">
            <div class="spinner"></div>
          </div>
          <img 
            :src="siteImage" 
            :alt="site.building.address_line_1" 
            class="site-image"
            @load="onImageLoad"
            @error="onImageError"
          />
        </div>
         
        <!-- Site Metrics Table -->
        <div class="site-stats-table">
          <table>
            <tbody>
              <tr v-if="site.size">
                <th>Site Size</th>
                <td>{{ formatNumber(site.size) }} sq ft</td>
              </tr>
              <tr v-if="site.current_psf_formatted">
                <th>Current PSF</th>
                <td>{{ formatCurrency(site.current_psf_formatted) }}</td>
              </tr>
              <tr v-if="site.utilization !== null && site.utilization !== undefined">
                <th>Utilization</th>
                <td>{{ site.utilization }}%</td>
              </tr>
              <tr v-if="site.shortened_remaining_lease_cost">
                <th>Remaining</th>
                <td>{{ site.shortened_remaining_lease_cost }}</td>
              </tr>
              <tr v-if="site.due_in_text">
                <th>Due In</th>
                <td>{{ site.due_in_text }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="site-popup-footer">
        <a :href="site.view_url" class="site-popup-link-button">
          View Full Details
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SitePopup',
  
  props: {
    site: {
      type: Object,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      imageLoading: true
    };
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset loading state when popup opens
        this.imageLoading = true;
      }
    }
  },
  
  computed: {
    siteImage() {
      // Check if site has an image
      if (this.site?.image) {
        // If image is an object with view_url property
        if (typeof this.site.image === 'object' && this.site.image.view_url) {
          return this.site.image.view_url;
        }
        // If image is a string URL
        if (typeof this.site.image === 'string') {
          return this.site.image;
        }
      }
      // Fallback to building image if available
      if (this.site?.building?.image) {
        return this.site.building.image;
      }
      return null;
    }
  },
  
  mounted() {
    // Add ESC key listener
    document.addEventListener('keydown', this.handleEscKey);
  },
  
  beforeDestroy() {
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleEscKey);
  },
  
  methods: {
    handleEscKey(event) {
      if (event.key === 'Escape' && this.show) {
        this.$emit('close');
      }
    },
    
    onImageLoad() {
      this.imageLoading = false;
    },
    
    onImageError() {
      this.imageLoading = false;
      console.error('Failed to load site image');
    },
    
    formatNumber(value) {
      return value?.toLocaleString('en-US') || 'N/A';
    },
    
    formatCurrency(value) {
      if (!value) return 'N/A';
      // If value is already formatted as currency, return it
      if (typeof value === 'string' && value.includes('$')) {
        return value;
      }
      // Otherwise format as currency
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  }
};
</script>
